import {Component, computed, DestroyRef, inject, Injector, OnInit, viewChild, ViewChild, signal, ChangeDetectionStrategy} from "@angular/core"
import {RouterOutlet, Router, ActivatedRoute} from "@angular/router"
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop"
import {from} from "rxjs"
import {CheckboxesFilterComponent, PinnedFilterComponent, PinnedFilterOptionComponent, TagSearchFilterComponent} from "@common/components/filters"
import {ItemFiltersComponent} from "@common/components/item"
import {TabsComponent} from "@common/components/tabs/tabs.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {OrganizationType} from "@generated"
import {Enums} from "@enums"
import {Labels, StateLabel} from "@labels"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {SidebarLayoutComponent} from "@platform/components/layouts/sidebar-layout/sidebar-layout.component"
import {PicturesGridComponent} from "@platform/components/pictures/pictures-grid/pictures-grid.component"
import {ProjectTreeComponent} from "@platform/components/pictures/project-tree/project-tree.component"
import {PicturesGridVisibleUsersGQL} from "@platform/components/pictures/pictures-grid/pictures-grid.generated"
import {IsDefined} from "@cm/utils/filter"
import {SiloService} from "@common/services/auth/silo.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-pictures-page",
    imports: [
        SidebarLayoutComponent,
        RouterOutlet,
        PicturesGridComponent,
        TabsComponent,
        ProjectTreeComponent,
        ItemFiltersComponent,
        TagSearchFilterComponent,
        PinnedFilterComponent,
        PinnedFilterOptionComponent,
        CheckboxesFilterComponent,
    ],
    templateUrl: "./pictures-page.component.html",
    styleUrl: "./pictures-page.component.scss",
})
export class PicturesPageComponent implements OnInit {
    readonly Enums = Enums
    readonly Labels = Labels

    readonly permission = inject(PermissionsService)
    readonly organizations = inject(OrganizationsService)
    readonly router = inject(Router)
    readonly route = inject(ActivatedRoute)
    readonly injector = inject(Injector)
    readonly visibleUsersGql = inject(PicturesGridVisibleUsersGQL)
    readonly destroyRef = inject(DestroyRef)
    readonly silo = inject(SiloService)

    $can = this.permission.$to

    @ViewChild(PicturesGridComponent) picturesGrid?: PicturesGridComponent

    readonly $tabs = signal<{title: string; icon: string; id: "projects" | "filters"}[]>([
        {
            title: "Projects",
            icon: "folder",
            id: "projects",
        },
        {
            title: "Filters",
            icon: "filter",
            id: "filters",
        },
    ])

    readonly $queryParams = toSignal(this.route.queryParams, {initialValue: {} as {[key: string]: string}})

    readonly $selectedTab = computed<"projects" | "filters">(() => {
        const params = this.$queryParams()
        if (["projects", "filters"].includes(params["tab"])) {
            return params["tab"]
        }
        const silo = this.silo.$silo()
        const orgType = silo?.organization?.type
        if (orgType === OrganizationType.EnterpriseCloud) {
            return "projects"
        }
        return "filters"
    })

    readonly $organization = computed<{id: string} | null | undefined>(() => {
        const params = this.$queryParams()
        if (params["organizationId"]) {
            return this.organizations.$byId(params["organizationId"])()
        } else {
            // need to wait for the organizations to be loaded
            const organizationsLoaded = this.organizations.$initialLoadCompleted()
            if (organizationsLoaded) {
                return this.$can().read.organization() ? undefined : this.organizations.$current()
            } else {
                return undefined
            }
        }
    })

    readonly $setId = computed<string | undefined>(() => {
        const params = this.$queryParams()
        if (this.$selectedTab() === "projects") {
            return params["setId"]
        }
        return undefined
    })

    readonly $projectId = computed<string | undefined>(() => {
        const params = this.$queryParams()
        if (this.$selectedTab() === "projects") {
            return params["projectId"]
        }
        return undefined
    })

    readonly $visibleUsers = signal<StateLabel<string>[] | null>(null)
    readonly $projectTree = viewChild(ProjectTreeComponent)

    ngOnInit() {
        // Load visible users for filters
        from(fetchThrowingErrors(this.visibleUsersGql)({}))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({users}) => {
                this.$visibleUsers.set(
                    users.filter(IsDefined).map((user) => ({
                        label: user.name,
                        state: user.id,
                    })),
                )
            })
    }

    async onChangeTabEvent(selectedTab: "projects" | "filters") {
        const currentOrganizationId = this.$organization()?.id
        await this.router.navigate([], {
            queryParams: {tab: selectedTab, organizationId: currentOrganizationId, setId: null, projectId: null},
            queryParamsHandling: "merge",
        })
    }
}
